import React from 'react';
import { createPortal } from 'react-dom';
import type { WebhookImageArtifact } from './alertEventPayload';
import './EventPayloadModal.css';

interface EventPayloadModalProps {
  payload: string;
  eventHeading: string;
  trigger: HTMLButtonElement;
  imageArtifact?: WebhookImageArtifact;
  onClose: () => void;
}

export function EventPayloadModal({ payload, eventHeading, trigger, imageArtifact, onClose }: EventPayloadModalProps) {
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const titleId = React.useId();
  const [copyStatus, setCopyStatus] = React.useState('');
  const [copying, setCopying] = React.useState(false);
  const [themeStyle] = React.useState(() => {
    const computed = getComputedStyle(trigger);
    const style: Record<string, string> = { fontFamily: computed.fontFamily };
    for (const token of ['surface', 'background', 'border', 'text-primary', 'text-secondary', 'primary', 'on-primary']) {
      const name = `--color-${token}`;
      const value = computed.getPropertyValue(name).trim();
      if (value) style[name] = value;
    }
    return style as React.CSSProperties;
  });

  React.useEffect(() => {
    const overlay = dialogRef.current?.parentElement;
    const siblings = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlay);
    const previousInert = siblings.map(element => element.inert);
    const previousOverflow = document.body.style.overflow;
    siblings.forEach(element => { element.inert = true; });
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const focusInside = (event: FocusEvent) => {
      if (!dialogRef.current?.contains(event.target as Node)) closeRef.current?.focus();
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current();
      }
      if (event.key === 'Tab') {
        const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], [tabindex="0"]') ?? []);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey, true);
    document.addEventListener('focusin', focusInside);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      document.removeEventListener('focusin', focusInside);
      siblings.forEach((element, index) => { element.inert = previousInert[index]; });
      document.body.style.overflow = previousOverflow;
      if (trigger.isConnected) trigger.focus();
    };
  }, [trigger]);

  const copyPayload = async () => {
    setCopying(true);
    setCopyStatus('');
    try {
      await navigator.clipboard.writeText(payload);
      setCopyStatus('Payload copied.');
    } catch {
      setCopyStatus('Could not copy payload. Select and copy the payload manually.');
    } finally {
      setCopying(false);
    }
  };

  return createPortal(
    <div className="event-payload-overlay" style={themeStyle}
      data-theme={trigger.closest('[data-theme]')?.getAttribute('data-theme') ?? undefined}
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div ref={dialogRef} className="event-payload-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="event-payload-header">
          <h2 id={titleId}>{eventHeading} Payload</h2>
          <button ref={closeRef} type="button" className="event-payload-close" aria-label="Close payload" title="Close payload" onClick={onClose}>&times;</button>
        </header>
        {imageArtifact && (
          <figure className="event-payload-image">
            <img src={imageArtifact.dataUri} alt={imageArtifact.artifactName} />
            <figcaption>
              <span>{imageArtifact.artifactName} · {imageArtifact.contentType}</span>
              <a href={imageArtifact.dataUri} download={imageArtifact.downloadFileName}>Download image</a>
            </figcaption>
          </figure>
        )}
        <pre tabIndex={0} aria-label="Event payload">{payload}</pre>
        <footer className="event-payload-footer">
          <span role="status" aria-live="polite">{copyStatus}</span>
          <button type="button" onClick={copyPayload} disabled={copying}>{copying ? 'Copying...' : 'Copy Payload'}</button>
        </footer>
      </div>
    </div>, document.body
  );
}